import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { apiClient, formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import BeneficiariosEditor from "@/components/BeneficiariosEditor";
import { getBrand } from "@/lib/brand";

const LOGO = "/angel-logo.png";

const STEPS = [
  { id: 1, title: "Cuenta", desc: "Datos del titular" },
  { id: 2, title: "Beneficiarios", desc: "Familiares en México" },
  { id: 3, title: "Plan y pago", desc: "Activa tu membresía" },
];

export default function Onboarding() {
  const { user, setUser, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const brand = getBrand();
  const PLANES = brand.planes;
  const cur = brand.currency;
  const sym = brand.currencySymbol || "$";

  const beneficiariosCount = user?.beneficiarios?.length || 0;
  const isActive = user?.membership_status === "active";
  // Determine current step
  const initialStep = isActive ? 3 : (beneficiariosCount > 0 ? 3 : 2);
  const [step, setStep] = useState(initialStep);
  const [loadingPlan, setLoadingPlan] = useState(null);

  useEffect(() => {
    if (isActive) {
      toast.success("¡Tu membresía ya está activa!");
      navigate("/cuenta");
    }
  }, [isActive, navigate]);

  const handleBenefSaved = (updatedUser) => {
    setUser(updatedUser);
    setStep(3);
  };

  const elegirPlan = async (plan, term = "mensual") => {
    if (!user?.beneficiarios?.length) {
      toast.error("Primero agrega al menos un beneficiario.");
      setStep(2);
      return;
    }
    if (user.beneficiarios.length > plan.maxBeneficiarios) {
      toast.error(`Este plan permite máximo ${plan.maxBeneficiarios} beneficiario(s). Tienes ${user.beneficiarios.length}.`);
      return;
    }
    setLoadingPlan(plan.id + term);
    try {
      const { data } = await apiClient.post("/checkout/session", { plan_id: plan.id, origin_url: window.location.origin, term });
      const value = term === "msi12" ? parseFloat(plan.precio) * 12 : parseFloat(plan.precio);
      trackEvent("begin_checkout", {
        currency: cur,
        value,
        items: [{ item_id: plan.id, item_name: plan.nombre, price: value, quantity: 1 }],
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setLoadingPlan(null);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  const maxBenef = 4; // Pre-payment: allow max across all plans

  return (
    <div className="onboard-page">
      <header className="onboard-header">
        <a href="/"><img src={LOGO} alt={brand.name} className="onboard-logo" /></a>
        <button onClick={handleLogout} className="onboard-logout" data-testid="onboard-logout">
          <i className="fas fa-sign-out-alt" /> Salir
        </button>
      </header>

      <div className="onboard-container">
        {/* Stepper */}
        <div className="stepper" data-testid="stepper">
          {STEPS.map((s, i) => {
            const completed = s.id < step || (s.id === 1) || (s.id === 2 && beneficiariosCount > 0) || (s.id === 3 && isActive);
            const active = s.id === step;
            return (
              <div key={s.id} className={`step ${active ? "step-active" : ""} ${completed ? "step-done" : ""}`} data-testid={`step-${s.id}`}>
                <div className="step-circle">
                  {completed && !active ? <i className="fas fa-check" /> : s.id}
                </div>
                <div className="step-meta">
                  <strong>{s.title}</strong>
                  <span>{s.desc}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`step-line ${completed ? "step-line-done" : ""}`} />}
              </div>
            );
          })}
        </div>

        {/* Step content */}
        <div className="onboard-panel">
          {step === 1 && (
            <div className="onboard-step" data-testid="onboard-step-1">
              <h2><i className="fas fa-check-circle" style={{ color: "#7DC242", marginRight: 8 }} /> Cuenta creada</h2>
              <p>¡Bienvenido <strong>{user?.nombre}</strong>! Tu cuenta ya está lista. Continúa con los datos de las personas en México que vas a proteger.</p>
              <button className="btn btn-primary" onClick={() => setStep(2)} data-testid="continue-step-2">
                Continuar <i className="fas fa-arrow-right" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="onboard-step" data-testid="onboard-step-2">
              <h2>Tus beneficiarios en México</h2>
              <p className="text-muted">Datos críticos para poder despachar grúa, ambulancia o legal cuando lo necesiten. Puedes agregar hasta 4 personas (depende del plan).</p>
              <BeneficiariosEditor user={user} maxAllowed={maxBenef} onSaved={handleBenefSaved} />
              <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between" }}>
                <button className="btn btn-secondary" onClick={() => setStep(1)} data-testid="back-step-1">
                  <i className="fas fa-arrow-left" /> Atrás
                </button>
                {beneficiariosCount > 0 && (
                  <button className="btn btn-primary" onClick={() => setStep(3)} data-testid="continue-step-3">
                    Continuar al plan <i className="fas fa-arrow-right" />
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="onboard-step" data-testid="onboard-step-3">
              <h2>Elige tu plan</h2>
              <p className="text-muted">Activa tu membresía con cobro membresía transfronteriza vía Stripe. Puedes cancelar cuando quieras.</p>

              {beneficiariosCount === 0 && (
                <div className="alert-card" style={{ marginBottom: 16 }}>
                  <i className="fas fa-exclamation-triangle" />
                  <div>
                    <strong>Primero agrega un beneficiario.</strong>
                    <p>Sin esto no podemos enviar servicio a nadie en México.</p>
                  </div>
                  <button className="btn btn-primary" onClick={() => setStep(2)}>Ir al paso 2</button>
                </div>
              )}

              <div className="planes-grid">
                {PLANES.map((p) => {
                  const disabledByLimit = beneficiariosCount > p.maxBeneficiarios;
                  return (
                    <div key={p.id} className={`plan-card ${p.featured ? "featured" : ""}`} data-testid={`onboard-plan-${p.id}`}>
                      {p.featured && <div className="plan-badge">MÁS POPULAR</div>}
                      <div className="plan-header"><h3>{p.nombre}</h3></div>
                      {p.promoBadge && (
                        <div style={{ textAlign: "center", marginTop: 8 }}>
                          <span style={{ display: "inline-block", background: "#E8F5D9", color: "#5BA628", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{p.promoBadge}</span>
                        </div>
                      )}
                      <div className="plan-price"><span className="currency">{cur} {sym}</span><span className="amount">{p.precio}</span><span className="period">{p.periodo || "/mes"}</span></div>
                      <div style={{ textAlign: "center", fontSize: 12, color: "#6b7280", marginTop: -4, marginBottom: 4 }}>Precio mensual <strong>+ IVA</strong></div>
                      <p style={{ textAlign: "center", fontSize: 12, color: p.maxBeneficiarios >= beneficiariosCount ? "#5BA628" : "#E84C5E", margin: "4px 0 8px" }}>
                        Permite hasta {p.maxBeneficiarios} beneficiario(s) · Tienes {beneficiariosCount}
                      </p>
                      <ul className="plan-features">
                        {p.features.map((f, i) => (
                          <li key={i} style={{ opacity: f.ok === false ? 0.45 : 1 }}>
                            <i className={f.ok === false ? "fas fa-times" : "fas fa-check"} style={f.ok === false ? { color: "#cbd5e1" } : undefined} />
                            {typeof f === "string" ? f : f.text}
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => elegirPlan(p, "mensual")}
                        disabled={loadingPlan === p.id + "mensual" || disabledByLimit || beneficiariosCount === 0}
                        className="btn btn-primary plan-btn"
                        data-testid={`onboard-elegir-${p.id}`}>
                        {loadingPlan === p.id + "mensual" ? "Procesando..." : disabledByLimit ? "Excede beneficiarios" : "Pagar mensual"}
                      </button>
                      <div style={{ marginTop: 10, padding: "10px 12px", background: "#F0F7FF", border: "1px dashed #163680", borderRadius: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#163680" }}>
                          <i className="fas fa-credit-card" style={{ marginRight: 6 }} />12 meses sin intereses
                        </div>
                        <div style={{ fontSize: 12, color: "#374151", margin: "4px 0 8px" }}>
                          <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>{cur} {sym}{(Number(p.precio) * 12).toLocaleString("es-MX")}</span>{" → "}
                          12 pagos de {cur} {sym}{(Number(p.precio) / 2).toLocaleString("es-MX")} = <strong>{cur} {sym}{(Number(p.precio) * 6).toLocaleString("es-MX")}</strong> en total <strong>+ IVA</strong>.
                          <br /><span style={{ color: "#DC2626", fontWeight: 800, textTransform: "uppercase" }}>Págalo a meses con tu tarjeta de crédito, sin intereses y recibe el 50% de descuento</span>
                        </div>
                        <button
                          onClick={() => elegirPlan(p, "msi12")}
                          disabled={loadingPlan === p.id + "msi12" || disabledByLimit || beneficiariosCount === 0}
                          className="btn plan-btn"
                          data-testid={`onboard-msi12-${p.id}`}
                          style={{ width: "100%", background: "#163680", color: "white" }}>
                          {loadingPlan === p.id + "msi12" ? "Procesando..." : "Pagar a 12 MSI"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 16 }}>
                <button className="btn btn-secondary" onClick={() => setStep(2)} data-testid="back-step-2">
                  <i className="fas fa-arrow-left" /> Atrás
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="onboard-skip">
          ¿Prefieres explorar primero? <button onClick={() => navigate("/cuenta")} className="link-btn" data-testid="skip-onboarding">Ir al portal sin activar</button>
        </p>
      </div>
    </div>
  );
}
